import client from "@/client/publicApiClient.ts";
import { useQuery, queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import type { GetFixSessionCredsQueryResponse, GetFixSessionCredsPathParams, GetFixSessionCreds404 } from "../types/GetFixSessionCreds";
import type { QueryObserverOptions, UseQueryResult, QueryKey, UseSuspenseQueryOptions, UseSuspenseQueryResult } from "@tanstack/react-query";

 type GetFixSessionCredsClient = typeof client<GetFixSessionCredsQueryResponse, GetFixSessionCreds404, never>;
type GetFixSessionCreds = {
    data: GetFixSessionCredsQueryResponse;
    error: GetFixSessionCreds404;
    request: never;
    pathParams: GetFixSessionCredsPathParams;
    queryParams: never;
    headerParams: never;
    response: GetFixSessionCredsQueryResponse;
    client: {
        parameters: Partial<Parameters<GetFixSessionCredsClient>[0]>;
        return: Awaited<ReturnType<GetFixSessionCredsClient>>;
    };
};
export const getFixSessionCredsQueryKey = (clientCode: GetFixSessionCredsPathParams["clientCode"], fixSessionId: GetFixSessionCredsPathParams["fixSessionId"]) => [{ url: "/api/clients/:clientCode/fix-sessions/:fixSessionId/creds", params: { clientCode: clientCode, fixSessionId: fixSessionId } }] as const;
export type GetFixSessionCredsQueryKey = ReturnType<typeof getFixSessionCredsQueryKey>;
export function getFixSessionCredsQueryOptions(clientCode: GetFixSessionCredsPathParams["clientCode"], fixSessionId: GetFixSessionCredsPathParams["fixSessionId"], options: GetFixSessionCreds["client"]["parameters"] = {}) {
    const queryKey = getFixSessionCredsQueryKey(clientCode, fixSessionId);
    return queryOptions({
        queryKey,
        queryFn: async () => {
            const res = await client<GetFixSessionCreds["data"], GetFixSessionCreds["error"]>({
                method: "get",
                url: `/api/clients/${clientCode}/fix-sessions/${fixSessionId}/creds`,
                ...options
            });
            return res.data;
        },
    });
}
/**
 * @summary Get credentials
 * @link /api/clients/:clientCode/fix-sessions/:fixSessionId/creds
 */
export function useGetFixSessionCreds<TData = GetFixSessionCreds["response"], TQueryData = GetFixSessionCreds["response"], TQueryKey extends QueryKey = GetFixSessionCredsQueryKey>(clientCode: GetFixSessionCredsPathParams["clientCode"], fixSessionId: GetFixSessionCredsPathParams["fixSessionId"], options: {
    query?: Partial<QueryObserverOptions<GetFixSessionCreds["response"], GetFixSessionCreds["error"], TData, TQueryData, TQueryKey>>;
    client?: GetFixSessionCreds["client"]["parameters"];
} = {}): UseQueryResult<TData, GetFixSessionCreds["error"]> & {
    queryKey: TQueryKey;
} {
    const { query: queryOptions, client: clientOptions = {} } = options ?? {};
    const queryKey = queryOptions?.queryKey ?? getFixSessionCredsQueryKey(clientCode, fixSessionId);
    const query = useQuery({
        ...getFixSessionCredsQueryOptions(clientCode, fixSessionId, clientOptions) as unknown as QueryObserverOptions,
        queryKey,
        ...queryOptions as unknown as Omit<QueryObserverOptions, "queryKey">
    }) as UseQueryResult<TData, GetFixSessionCreds["error"]> & {
        queryKey: TQueryKey;
    };
    query.queryKey = queryKey as TQueryKey;
    return query;
}
export const getFixSessionCredsSuspenseQueryKey = (clientCode: GetFixSessionCredsPathParams["clientCode"], fixSessionId: GetFixSessionCredsPathParams["fixSessionId"]) => [{ url: "/api/clients/:clientCode/fix-sessions/:fixSessionId/creds", params: { clientCode: clientCode, fixSessionId: fixSessionId } }] as const;
export type GetFixSessionCredsSuspenseQueryKey = ReturnType<typeof getFixSessionCredsSuspenseQueryKey>;
export function getFixSessionCredsSuspenseQueryOptions(clientCode: GetFixSessionCredsPathParams["clientCode"], fixSessionId: GetFixSessionCredsPathParams["fixSessionId"], options: GetFixSessionCreds["client"]["parameters"] = {}) {
    const queryKey = getFixSessionCredsSuspenseQueryKey(clientCode, fixSessionId);
    return queryOptions({
        queryKey,
        queryFn: async () => {
            const res = await client<GetFixSessionCreds["data"], GetFixSessionCreds["error"]>({
                method: "get",
                url: `/api/clients/${clientCode}/fix-sessions/${fixSessionId}/creds`,
                ...options
            });
            return res.data;
        },
    });
}
/**
 * @summary Get credentials
 * @link /api/clients/:clientCode/fix-sessions/:fixSessionId/creds
 */
export function useGetFixSessionCredsSuspense<TData = GetFixSessionCreds["response"], TQueryKey extends QueryKey = GetFixSessionCredsSuspenseQueryKey>(clientCode: GetFixSessionCredsPathParams["clientCode"], fixSessionId: GetFixSessionCredsPathParams["fixSessionId"], options: {
    query?: Partial<UseSuspenseQueryOptions<GetFixSessionCreds["response"], GetFixSessionCreds["error"], TData, TQueryKey>>;
    client?: GetFixSessionCreds["client"]["parameters"];
} = {}): UseSuspenseQueryResult<TData, GetFixSessionCreds["error"]> & {
    queryKey: TQueryKey;
} {
    const { query: queryOptions, client: clientOptions = {} } = options ?? {};
    const queryKey = queryOptions?.queryKey ?? getFixSessionCredsSuspenseQueryKey(clientCode, fixSessionId);
    const query = useSuspenseQuery({
        ...getFixSessionCredsSuspenseQueryOptions(clientCode, fixSessionId, clientOptions) as unknown as QueryObserverOptions,
        queryKey,
        ...queryOptions as unknown as Omit<QueryObserverOptions, "queryKey">
    }) as UseSuspenseQueryResult<TData, GetFixSessionCreds["error"]> & {
        queryKey: TQueryKey;
    };
    query.queryKey = queryKey as TQueryKey;
    return query;
}